import { useQueryClient } from '@tanstack/react-query';
import { getCustomerNotes } from '@/lib/demo-data/customer-notes';
import type {
  CreateNoteRequest,
  CustomerNote,
  CustomerNoteApiItem,
  CustomerNotesApiResponse,
} from '@/types/customer-note';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

function mapApiItemToCustomerNote(item: CustomerNoteApiItem): CustomerNote {
  return {
    id: String(item.id),
    customerId: String(item.customerId),
    content: item.content,
    createdAt: item.createdAt,
    createdBy: {
      id: String(item.createdByUserId),
      name: item.createdByUserName,
    },
  };
}

/**
 * Hook to fetch customer notes
 * Handles both Demo and Live modes
 */
export function useCustomerNotes(customerId: string | undefined) {
  const { apiGet } = useApi();
  const mockNotes = customerId ? getCustomerNotes(customerId) : [];

  return useModeAwareQuery<CustomerNote[], Error>(
    ['customer-notes', customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      const response = await apiGet<CustomerNotesApiResponse>(
        `/api/v1/customers/${customerId}/notes`,
        undefined,
        signal,
      );
      return (response.notes || []).map(mapApiItemToCustomerNote);
    },
    mockNotes,
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

/**
 * Hook to add a new customer note
 * Handles both Demo and Live modes
 */
export function useAddCustomerNote() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CustomerNote, Error, CreateNoteRequest & { userId: string; userName: string }>(
    async (request) => {
      const response = await apiPost<CustomerNoteApiItem>(`/api/v1/customers/${request.customerId}/notes`, {
        content: request.content,
        transactionId: request.transactionId,
      });
      return mapApiItemToCustomerNote(response);
    },
    (request) => {
      return {
        id: String(Date.now()),
        customerId: request.customerId,
        content: request.content,
        createdAt: new Date().toISOString(),
        createdBy: {
          id: request.userId,
          name: request.userName,
        },
      };
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['customer-notes', variables.customerId] });
        queryClient.invalidateQueries({ queryKey: ['customer-audit-logs'] });
      },
    },
  );
}
